import api from './api';
import { getAccessToken } from './tokenStore';
import { blobHolen } from './download';

/**
 * Medienbibliothek (Backend: /api/media-assets). Videos, PDF-Karussells,
 * Bilder und Office-Dokumente des ganzen Mandanten.
 *
 * Hochladen, zwei Wege:
 * - bis TEIL_GRENZE (8 MiB): ein POST per XMLHttpRequest, der Body ist die
 *   Datei selbst (kein multipart). Token wie beim api-Client aus dem
 *   Speicher (tokenStore.ts); bei 401 frischt ein api-Aufruf es auf, dann ein zweiter
 *   Versuch.
 * - groesser: Teil-Upload (POST /uploads, PUT je Teil, POST complete). Der
 *   Traefik-Entrypoint bricht jeden Request nach 60 s ab; ein 392-MB-Video
 *   am Stueck schafft das nur mit ueber 50 Mbit/s Upload. Jeder Teil ist
 *   hoechstens 8 MiB gross, ein Fehlschlag (Netz, 5xx) wird einmal
 *   wiederholt. Diese Aufrufe gehen ueber axios (Token-Auffrischung inklusive).
 */

export type MediaKind = 'video' | 'pdf' | 'image' | 'document';
export type MediaProfile = 'david' | 'florian' | 'neutral';
export type VorschauStatus = 'ok' | 'fehler' | 'keine';
export type FassungArt = 'linkedin' | 'reel' | 'x' | 'instagram';
export type FassungStatus = 'ausstehend' | 'ok' | 'fehler' | 'nicht_noetig';

/**
 * Plattform-Fassung eines Mediums (Backend: media-fassung.service.ts):
 * entsteht nach dem Hochladen im Hintergrund und wird beim Veroeffentlichen
 * automatisch genommen.
 */
export interface MediaFassung {
  id: string;
  art: FassungArt;
  /** PDF-Seite (1..10) bei Instagram aus einem PDF, sonst 0. */
  seite: number;
  status: FassungStatus;
  fehler?: string | null;
  mimeType?: string | null;
  sizeBytes?: number | null;
  breite?: number | null;
  hoehe?: number | null;
  dauerSek?: number | null;
  updatedAt?: string;
}

/** Antwort auf GET/POST /media-assets/:id/fassungen. */
export interface FassungenAntwort {
  fassungen: MediaFassung[];
  /** Medium wartet in der Warteschlange oder wird gerade bearbeitet. */
  wartet: boolean;
}

export const FASSUNG_LABELS: Record<FassungArt, string> = {
  linkedin: 'LinkedIn (unter 200 MB)',
  reel: 'Instagram-Reel (9:16)',
  x: 'X (bis 140 s)',
  instagram: 'Instagram-Bild',
};

export const FASSUNG_STATUS_LABELS: Record<FassungStatus, string> = {
  ausstehend: 'ausstehend',
  ok: 'fertig',
  fehler: 'Fehler',
  nicht_noetig: 'nicht nötig',
};

/**
 * Hinweis fuer Kachel und Zeile: 'ausstehend', solange Fassungen fehlen oder
 * laufen, 'fehler', wenn eine fehlgeschlagen ist, sonst null. Ohne Angabe
 * (aeltere Antwort) und fuer Dokumente: null.
 */
export function fassungsHinweis(asset: Pick<MediaAsset, 'kind' | 'fassungen'>): 'ausstehend' | 'fehler' | null {
  if (asset.kind === 'document' || !asset.fassungen) return null;
  if (asset.fassungen.some((f) => f.status === 'fehler')) return 'fehler';
  if (asset.fassungen.length === 0 || asset.fassungen.some((f) => f.status === 'ausstehend')) return 'ausstehend';
  return null;
}

/** Antwort auf POST /media-assets/:id/vorschau. */
export interface VorschauErgebnis {
  id: string;
  vorschauStatus: VorschauStatus | null;
  vorschauFehler: string | null;
  /** Dauert laenger (grosses Video): laeuft im Hintergrund weiter. */
  laeuft: boolean;
}

export interface MediaAsset {
  id: string;
  userId: string;
  tenantId?: string | null;
  scopeKey: string;
  kind: MediaKind;
  mimeType: string;
  sizeBytes: number;
  originalName: string;
  sha256: string;
  title?: string | null;
  topic?: string | null;
  profile?: MediaProfile | null;
  source?: string | null;
  postIdeaId?: string | null;
  postIdea?: { id: string; title: string } | null;
  /** null = noch nicht erzeugt, ok, fehler, keine (Office-Dokumente). */
  vorschauStatus?: VorschauStatus | null;
  vorschauFehler?: string | null;
  /** Nur in der Liste: Stand der Plattform-Fassungen. */
  fassungen?: Array<Pick<MediaFassung, 'art' | 'seite' | 'status'>>;
  createdAt: string;
  updatedAt: string;
  /** Nur in der Antwort auf einen Upload: die Datei lag schon vor. */
  duplicate?: boolean;
}

export interface MediaFilter {
  topic?: string;
  kind?: MediaKind | '';
  profile?: MediaProfile | '';
  postIdeaId?: string;
  q?: string;
}

export interface MediaUploadMeta {
  title?: string;
  topic?: string;
  profile?: MediaProfile | '';
  source?: string;
  postIdeaId?: string;
}

export interface MediaUpdate {
  title?: string | null;
  topic?: string | null;
  profile?: MediaProfile | null;
  postIdeaId?: string | null;
}

/** Wie im Backend (ERLAUBTE_TYPEN in media-asset.service.ts). */
export const MEDIA_MIME_TYPES: Readonly<Record<string, MediaKind>> = {
  'video/mp4': 'video',
  'video/quicktime': 'video',
  'application/pdf': 'pdf',
  'image/png': 'image',
  'image/jpeg': 'image',
  'image/webp': 'image',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'document',
  'application/vnd.oasis.opendocument.text': 'document',
};

/** Browser liefern fuer manche Endungen keinen oder einen anderen MIME-Typ. */
const MIME_JE_ENDUNG: Record<string, string> = {
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  odt: 'application/vnd.oasis.opendocument.text',
};

export const MEDIA_ACCEPT = Object.keys(MEDIA_MIME_TYPES).join(',') + ',.mp4,.mov,.pdf,.png,.jpg,.jpeg,.webp,.docx,.xlsx,.odt';

export const KIND_LABELS: Record<MediaKind, string> = {
  video: 'Video',
  pdf: 'PDF',
  image: 'Bild',
  document: 'Dokument',
};

export const PROFILE_LABELS: Record<MediaProfile, string> = {
  david: 'David',
  florian: 'Florian',
  neutral: 'Neutral',
};

/** MIME-Typ einer Datei; leer, wenn er nicht erlaubt ist. */
export function mimeVon(datei: { name: string; type: string }): string {
  const typ = (datei.type || '').toLowerCase();
  if (MEDIA_MIME_TYPES[typ]) return typ;
  const endung = datei.name.split('.').pop()?.toLowerCase() ?? '';
  return MIME_JE_ENDUNG[endung] ?? '';
}

export function groesseLesbar(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/** Ab dieser Groesse laeuft der Teil-Upload (gleich der Teilgroesse im Backend). */
export const TEIL_GRENZE = 8 * 1024 * 1024;

interface UploadStart {
  uploadId: string;
  partSize: number;
  partCount: number;
}

function ohneLeere(werte: Record<string, string | undefined>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(werte)) if (v) out[k] = v;
  return out;
}

class UploadFehler extends Error {
  constructor(message: string, public status: number) {
    super(message);
    this.name = 'UploadFehler';
  }
}

function xhrUpload(
  url: string,
  datei: File,
  mime: string,
  onProgress?: (anteil: number) => void,
): Promise<MediaAsset> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.setRequestHeader('Content-Type', mime);
    const token = getAccessToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      let body: any = null;
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        body = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body as MediaAsset);
      } else {
        const text = body?.error || body?.message || `Hochladen fehlgeschlagen (HTTP ${xhr.status})`;
        reject(new UploadFehler(text, xhr.status));
      }
    };
    xhr.onerror = () => reject(new UploadFehler('Verbindung beim Hochladen abgebrochen. Bitte erneut versuchen.', 0));
    xhr.onabort = () => reject(new UploadFehler('Hochladen abgebrochen.', 0));
    xhr.send(datei);
  });
}

const fehlerText = (err: any, ersatz: string): string =>
  err?.response?.data?.error || err?.message || ersatz;

/** Netzwerkfehler (keine Antwort) oder 5xx: lohnt einen zweiten Versuch. */
const wiederholbar = (err: any): boolean => !err?.response || err.response.status >= 500;

async function teilUpload(
  datei: File,
  mime: string,
  meta: MediaUploadMeta,
  onProgress?: (anteil: number) => void,
): Promise<MediaAsset> {
  let start: UploadStart;
  try {
    const { data } = await api.post('/media-assets/uploads', {
      filename: datei.name,
      mimeType: mime,
      size: datei.size,
      ...ohneLeere({
        title: meta.title,
        topic: meta.topic,
        profile: meta.profile || undefined,
        source: meta.source || 'upload',
        postIdeaId: meta.postIdeaId,
      }),
    });
    start = data;
  } catch (err: any) {
    throw new UploadFehler(fehlerText(err, 'Der Upload konnte nicht gestartet werden.'), err?.response?.status ?? 0);
  }

  const { uploadId, partSize, partCount } = start;
  let fertig = 0;
  try {
    for (let n = 1; n <= partCount; n++) {
      const stueck = datei.slice((n - 1) * partSize, Math.min(n * partSize, datei.size));
      const bisher = fertig;
      const senden = () => api.put(`/media-assets/uploads/${uploadId}/parts/${n}`, stueck, {
        headers: { 'Content-Type': 'application/octet-stream' },
        onUploadProgress: (e: { loaded: number }) => onProgress?.(Math.min(1, (bisher + e.loaded) / datei.size)),
      });
      try {
        await senden();
      } catch (err: any) {
        if (!wiederholbar(err)) throw err;
        // Kam der erste Versuch doch an, nimmt das Backend die Wiederholung
        // des letzten Teils idempotent an.
        await senden();
      }
      fertig += stueck.size;
      onProgress?.(fertig / datei.size);
    }
    const { data } = await api.post(`/media-assets/uploads/${uploadId}/complete`);
    return data as MediaAsset;
  } catch (err: any) {
    // Best effort: Sitzung im Backend freigeben (sonst raeumt sie der Leerlauf-Takt nach 30 min ab).
    api.delete(`/media-assets/uploads/${uploadId}`).catch(() => undefined);
    throw new UploadFehler(fehlerText(err, 'Hochladen fehlgeschlagen.'), err?.response?.status ?? 0);
  }
}

export const mediaApi = {
  async list(filter: MediaFilter = {}): Promise<MediaAsset[]> {
    const { data } = await api.get('/media-assets', {
      params: ohneLeere({
        topic: filter.topic, kind: filter.kind || undefined, profile: filter.profile || undefined,
        postIdeaId: filter.postIdeaId, q: filter.q,
      }),
    });
    return Array.isArray(data) ? data : [];
  },

  async get(id: string): Promise<MediaAsset> {
    const { data } = await api.get(`/media-assets/${id}`);
    return data;
  },

  async update(id: string, aenderung: MediaUpdate): Promise<MediaAsset> {
    const { data } = await api.patch(`/media-assets/${id}`, aenderung);
    return data;
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/media-assets/${id}`);
  },

  /** Vorschaubild jetzt (neu) erzeugen (linkedin.bearbeiten). */
  async vorschauNeu(id: string): Promise<VorschauErgebnis> {
    const { data } = await api.post(`/media-assets/${id}/vorschau`);
    return data;
  },

  /** Plattform-Fassungen mit Stand. */
  async fassungen(id: string): Promise<FassungenAntwort> {
    const { data } = await api.get(`/media-assets/${id}/fassungen`);
    return { fassungen: Array.isArray(data?.fassungen) ? data.fassungen : [], wartet: !!data?.wartet };
  },

  /** Alle Fassungen neu erzeugen (linkedin.bearbeiten; laeuft im Hintergrund). */
  async fassungenNeu(id: string): Promise<FassungenAntwort> {
    const { data } = await api.post(`/media-assets/${id}/fassungen`);
    return { fassungen: Array.isArray(data?.fassungen) ? data.fassungen : [], wartet: !!data?.wartet };
  },

  /** Datei einer fertigen Fassung als Blob (mit Bearer). */
  async fassungInhalt(id: string, fassungId: string, download = false): Promise<Blob> {
    return blobHolen(`/media-assets/${id}/fassungen/${fassungId}/content`, download ? { download: '1' } : undefined);
  },

  /** Inhalt als Blob (mit Bearer). Fuer Vorschau per URL.createObjectURL. */
  async content(id: string, download = false): Promise<Blob> {
    return blobHolen(`/media-assets/${id}/content`, download ? { download: '1' } : undefined);
  },

  /**
   * Laedt eine Datei hoch. `onProgress` bekommt 0..1. Wirft mit deutschem
   * Text (Backend-Meldung), z. B. bei 413 (zu gross) oder 415 (Dateityp).
   */
  async upload(datei: File, meta: MediaUploadMeta = {}, onProgress?: (anteil: number) => void): Promise<MediaAsset> {
    const mime = mimeVon(datei);
    if (!mime) throw new UploadFehler(`${datei.name}: Dateityp wird nicht angenommen.`, 415);
    if (datei.size > TEIL_GRENZE) return teilUpload(datei, mime, meta, onProgress);
    const query = new URLSearchParams(ohneLeere({
      filename: datei.name,
      title: meta.title,
      topic: meta.topic,
      profile: meta.profile || undefined,
      source: meta.source || 'upload',
      postIdeaId: meta.postIdeaId,
    }));
    const url = `${API_BASE_URL}/media-assets?${query.toString()}`;
    try {
      return await xhrUpload(url, datei, mime, onProgress);
    } catch (err) {
      if (!(err instanceof UploadFehler) || err.status !== 401) throw err;
      // Token abgelaufen: ein normaler Aufruf ueber den api-Client frischt es
      // auf (Interceptor in api.ts); danach liegt das neue im tokenStore.
      await api.get('/auth/me');
      onProgress?.(0);
      return xhrUpload(url, datei, mime, onProgress);
    }
  },
};

export default mediaApi;
